import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import {
  assignPaymentMethod,
  AssignPaymentMethodMutationParams,
  AssignPaymentMethodMutationResponse,
} from '@queries/checkout/mutations/assignPaymentMethod';
import { useOrderState } from '@hooks/useOrderState';
import { Console } from '@utils';
import { useSegment } from '@hooks/useSegment';
import { LocalPaymentOptions } from './LocalPaymentOptions';
import { usePermissions } from '@hooks/usePermissions';
import {
  RootDiv,
  TitleText,
  OptionDiv,
  TextDiv,
  OptionTitleText,
  OptionDescriptionText,
  ArrowIconContainer,
  SpaceBetweenContainer,
  HorizontalLineFull,
} from '../styles/PaymentsOptions.styles';
import { AssistantPaymentOptions } from './AssistantPaymentOptions';
import { PaymentMethods } from './PaymentMethods';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { CardIcon } from './CardIcon';
import { OtherPaymentMethods, PaymentMethod } from '@interfaces';
import { useOrderAndPaymentInfo } from '../zustand/paymentMethodStore';
import { useLocalPaymentOptionsActions } from '../zustand/localPaymentOptionsStore';
import { useAuth } from '@hooks/useAuth';
import { BANCO_ESTADO_EMAILS_ALLOWED } from '@config/environment';
interface PaymentsOptionsProps {
  loading: boolean;
  quoteLoading: boolean;
  handleNext: (card?: boolean) => Promise<void>;
}

export function PaymentsOptions(props: PaymentsOptionsProps) {
  const { loading, quoteLoading, handleNext } = props;
  const { appUser: user } = useAuth();

  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const isAssistantUser = userCan('MANAGE_USER');
  const areBancoEstadoEmailsAllowed = BANCO_ESTADO_EMAILS_ALLOWED.length > 0;
  const isPermittedUser = areBancoEstadoEmailsAllowed
    ? BANCO_ESTADO_EMAILS_ALLOWED.includes(user?.email ?? '')
    : true;

  const { track } = useSegment();
  const orderState = useOrderState();

  const { orderUuid, order: currentOrder } = orderState;

  const { orders, ordersTotal, paymentMethods } = useOrderAndPaymentInfo();

  const { setMixPayments } = useLocalPaymentOptionsActions();

  const [assignMethod] = useMutation<
    AssignPaymentMethodMutationResponse,
    AssignPaymentMethodMutationParams
  >(assignPaymentMethod());

  const deliveryCost =
    orders?.reduce((acc, curr) => acc + curr.deliveryCost, 0) ?? 0;

  const changePaymentAmount = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const valueToNumber = Number(
        event.target.value.replace('$', '').replace(/\./g, ''),
      );
      if (
        Number.isInteger(+valueToNumber) &&
        +valueToNumber <= ordersTotal - deliveryCost
      ) {
        setMixPayments({
          CASH: +valueToNumber,
          CARD: ordersTotal - deliveryCost - +valueToNumber,
        });
      }
    },
    [deliveryCost, ordersTotal, setMixPayments],
  );

  const handleSelect = useCallback(
    async (
      e: unknown,
      paymentMethod: PaymentMethod,
      otherPaymentMethods?: OtherPaymentMethods,
    ) => {
      try {
        const uuid =
          otherPaymentMethods == OtherPaymentMethods.FINTOC
            ? '00000000-0000-0000-0000-00000001'
            : otherPaymentMethods == OtherPaymentMethods.BANCO_ESTADO
            ? '00000000-0000-0000-0000-00000005'
            : paymentMethod?.uuid;
        await assignMethod({
          variables: {
            params: { orderUuid, paymentMethodUuid: uuid },
          },
          optimisticResponse({ params: { paymentMethodUuid } }) {
            const paymentMethod = paymentMethods.find(
              (e) => e.uuid === paymentMethodUuid,
            );
            return {
              assignPaymentMethod: {
                __typename: 'Order',
                id: currentOrder?.id,
                uuid: orderUuid,
                paymentMethod: {
                  id:
                    otherPaymentMethods == OtherPaymentMethods.FINTOC
                      ? -2
                      : otherPaymentMethods == OtherPaymentMethods.BANCO_ESTADO
                      ? -6
                      : paymentMethod.id,
                },
              },
            };
          },
        });

        const intallmentsRedirect =
          paymentMethod?.type === 'ONE_CLICK' &&
          ['Visa', 'MasterCard'].includes(
            paymentMethod?.data?.creditCardType as string,
          );

        await handleNext(intallmentsRedirect);
      } catch (e) {
        Console.error('CheckoutPaymentMethod: unhandled error', {
          e: e as unknown,
        });
        Console.error('CheckoutPaymentMethod: variables', {
          paymentMethodUuid: paymentMethod?.uuid,
          orderUuid,
        });
      }
    },
    [assignMethod, currentOrder?.id, handleNext, orderUuid, paymentMethods],
  );

  useEffect(
    function handleSelectPaymentMethodEvent() {
      if (currentOrder?.paymentMethod?.uuid) {
        void track('payment select', {
          payment_method_type: currentOrder?.paymentMethod.type,
        });
      }
    },
    [track, currentOrder?.paymentMethod],
  );

  return (
    <>
      {isLocalVendor ? (
        <LocalPaymentOptions changePaymentAmount={changePaymentAmount} />
      ) : isAssistantUser ? (
        <AssistantPaymentOptions
          changePaymentAmount={changePaymentAmount}
          loading={loading}
          handleSelect={handleSelect}
        />
      ) : (
        <PaymentMethods
          loading={loading}
          quoteLoading={quoteLoading}
          handleSelect={handleSelect}
        />
      )}

      {!isLocalVendor && !isAssistantUser && (
        <>
          <TitleText>Otro método de pago</TitleText>
          <RootDiv>
            <OptionDiv
              quoteLoading={quoteLoading}
              onClick={(event) =>
                void handleSelect(event, null, OtherPaymentMethods.FINTOC)
              }
            >
              <CardIcon creditCardType="Fintoc" />
              <SpaceBetweenContainer>
                <TextDiv>
                  <OptionTitleText>Directo con tu banco</OptionTitleText>
                  <OptionDescriptionText light>
                    sin salir de la página
                  </OptionDescriptionText>
                </TextDiv>
                <ArrowIconContainer>
                  <ArrowForwardIosRoundedIcon color="primary" />
                </ArrowIconContainer>
              </SpaceBetweenContainer>
            </OptionDiv>
            {isPermittedUser && (
              <>
                <HorizontalLineFull />
                <label>
                  <OptionDiv
                    quoteLoading={quoteLoading}
                    onClick={(event) =>
                      void handleSelect(
                        event,
                        null,
                        OtherPaymentMethods.BANCO_ESTADO,
                      )
                    }
                  >
                    <CardIcon creditCardType={'BancoEstado'} />
                    <SpaceBetweenContainer>
                      <TextDiv>
                        <OptionTitleText>Banco Estado</OptionTitleText>
                        <OptionDescriptionText light>
                          Compraquí
                        </OptionDescriptionText>
                      </TextDiv>
                      <ArrowIconContainer>
                        <ArrowForwardIosRoundedIcon color="primary" />
                      </ArrowIconContainer>
                    </SpaceBetweenContainer>
                  </OptionDiv>
                </label>
              </>
            )}
          </RootDiv>
        </>
      )}
    </>
  );
}
